/**
 * ChartPreview Component
 * Renders chart previews at a fixed canonical coordinate system.
 * Uses ResizeObserver + CSS transform to uniformly scale the canonical
 * rendering (both HTML title and SVG) to fit any container size.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useData } from '../contexts/DataContext';
import { countSparseRows } from '../utils/transformData';
import BarChartPreview from './previews/BarChartPreview';
import ScatterPlotPreview from './previews/ScatterPlotPreview';
import LineChartPreview from './previews/LineChartPreview';
import BoxPlotPreview from './previews/BoxPlotPreview';
import HistogramPreview from './previews/HistogramPreview';
import LollipopPreview from './previews/LollipopPreview';
import DivergingBarPreview from './previews/DivergingBarPreview';
import HeatmapPreview from './previews/HeatmapPreview';
import ComboPreview from './previews/ComboPreview';
import RadarPreview from './previews/RadarPreview';

import { ASPECT_RATIO_PRESETS } from '../config/constants';
export { ASPECT_RATIO_PRESETS };

function ChartPreview({ chartType, config }) {
  const outerRef = useRef(null);
  const [avail, setAvail] = useState(null);
  const { resolved: theme } = useTheme();
  const { rawData, mapping } = useData();
  const ui = theme.ui || {};

  const sparseInfo = useMemo(() => {
    if (!rawData || !mapping || !chartType) return null;
    return countSparseRows(rawData, mapping, chartType.id);
  }, [rawData, mapping, chartType]);

  const preset = ASPECT_RATIO_PRESETS[config.aspectRatio] || ASPECT_RATIO_PRESETS.landscape;
  const width = preset.width;
  const height = preset.height;

  useEffect(() => {
    const el = outerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(entries => {
      const { width: cw, height: ch } = entries[0].contentRect;
      if (cw > 0 && ch > 0) setAvail({ w: cw, h: ch });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const bannerHeight = sparseInfo ? 30 : 0;
  const chartAvailH = avail ? Math.max(0, avail.h - bannerHeight) : 0;
  const scale = avail
    ? Math.min(avail.w / width, chartAvailH / height, 1)
    : 0;

  const previewProps = useMemo(
    () => ({ chartType, config, width, height }),
    [chartType, config, width, height],
  );

  const renderPreview = () => {
    switch (chartType.category) {
      case 'bar':
        return <BarChartPreview {...previewProps} />;
      
      case 'scatter':
        return <ScatterPlotPreview {...previewProps} />;
      
      case 'line':
        return <LineChartPreview {...previewProps} />;
      
      case 'distribution':
        return <BoxPlotPreview {...previewProps} />;
      
      case 'histogram':
        return <HistogramPreview {...previewProps} />;

      case 'lollipop':
        return <LollipopPreview {...previewProps} />;

      case 'diverging-bar':
        return <DivergingBarPreview {...previewProps} />;

      case 'heatmap':
        return <HeatmapPreview {...previewProps} />;

      case 'combo':
        return <ComboPreview {...previewProps} />;

      case 'radar':
        return <RadarPreview {...previewProps} />;

      default:
        return (
          <div 
            className="flex items-center justify-center"
            style={{ width, height, color: ui.emptyStateText }}
          >
            Preview not available for this chart type
          </div>
        );
    }
  };

  return (
    <div
      ref={outerRef}
      data-testid="chart-preview"
      className="w-full h-full flex flex-col items-center justify-center"
      style={{ backgroundColor: theme.background, overflow: 'hidden' }}
    >
      {sparseInfo && (
        <div
          className="text-xs px-3 py-1.5 mb-1 self-center"
          style={{ color: '#6b7280', backgroundColor: '#f3f4f6', borderRadius: 4 }}
        >
          {sparseInfo.skippedCount} of {sparseInfo.totalCount} rows had missing or non-numeric values and were excluded from the chart.
        </div>
      )}
      {scale === 0 && (
        <div className="flex items-center justify-center" style={{ color: ui.textSecondary }}>
          <span className="text-xs">Preparing chart...</span>
        </div>
      )}
      {scale > 0 && (
        <div style={{
          width: Math.round(width * scale),
          height: Math.round(height * scale),
          overflow: 'hidden',
        }}>
          <div style={{
            width,
            height,
            transform: `scale(${scale})`,
            transformOrigin: 'top left',
          }}>
            {renderPreview()}
          </div>
        </div>
      )}
    </div>
  );
}

export default ChartPreview;
